import type { Action } from "components/shared/ButtonMenu";
import ButtonMenu from "components/shared/ButtonMenu";
import CheckboxQuestion from "components/shared/CheckboxQuestion";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import InfoBlue from "components/shared/InfoBlockBlue";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import { IChannel } from "containers/Channels/types";
import { formatTimestampDates, transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import { formatNumber, getCleanStartorEndDay } from "helpers/utils";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import {
    convertTransferTermSale,
    ETransferTermSalesStatus,
    updateTransferTermSale,
} from "store/transferTermSalesStore";
import { ChannelKind } from "store/types";

import variables from "../../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

interface IPaymentDateModal {
    /**
     * Sale being updated.
     */
    termSale: ITransferTermSale | undefined;
    /**
     * Triggers closing of modal.
     */
    handleClose: ({
        updatedSale,
        updatedSales,
    }: {
        updatedSale?: ITransferTermSale;
        updatedSales?: ITransferTermSale[];
    }) => void;
    /**
     * If new, used to mark as paid. If edit, used to revert status back to WAITING_PAYMENT.
     */
    formMode: string;
    currentSales: ITransferTermSale[];
}

/**
 * Modal from TransferTermSalesTable which allows user to mark a sale as paid or revert this state if already there.
 */
const PaymentDateModal = ({ termSale, handleClose, formMode, currentSales }: IPaymentDateModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [submitPending, setSubmitPending] = useState(false);

    const formRef = useRef<HTMLFormElement | null>(null);

    const { channels } = useAppSelector((state) => state.channels);
    const rightsTransferChannels = channels.filter((channel) => channel.kind === ChannelKind.RIGHTS_TRANSFER);

    const channelOptions = useMemo(
        () => rightsTransferChannels.map((channel) => ({ value: channel.id, label: channel.name })),
        [rightsTransferChannels],
    );

    const [wantsTransferRights, setWantsTransferRights] = useState(true);
    const [wantsAppliedToAll, setWantsAppliedToAll] = useState(false);

    // Rebuild a local Date from the UTC calendar parts so the picker shows the day
    // the backend stored, not the previous day when the user sits west of UTC.
    const defaultValues = {
        status: ETransferTermSalesStatus.PAYED,
        paymentDate: formMode === "edit" && termSale?.paymentDate ? getCleanStartorEndDay(termSale.paymentDate) : null,
        channelId: channelOptions.length ? channelOptions[0].value : null,
    };

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({ defaultValues, mode: "all" });

    const selectOptions = [
        { value: 5, label: t("ConnectTitle.Yes") },
        { value: 6, label: t("ConnectTitle.No") },
    ];

    const onSubmit = async (data) => {
        setSubmitPending(true);
        if (termSale) {
            const updatedSale = {
                ...termSale,
                paymentDate:
                    data.status === ETransferTermSalesStatus.WAITING_PAYMENT
                        ? null
                        : formatTimestampDates(data.paymentDate) ?? formatTimestampDates(new Date()),
                status: formMode === "new" ? ETransferTermSalesStatus.PAYED : data.status,
            };
            const response = await dispatch(
                updateTransferTermSale({ saleId: termSale.id, data: updatedSale, applyToGroup: wantsAppliedToAll }),
            );
            if (response.meta.requestStatus === "fulfilled") {
                setSubmitPending(false);
                if (wantsTransferRights) {
                    dispatch(
                        convertTransferTermSale({
                            saleId: termSale?.id,
                            channelId: data.channelId,
                            applyToGroup: wantsAppliedToAll,
                        }),
                    );
                }

                const newSales = currentSales.map((sale) => {
                    if (sale.id === updatedSale.id) {
                        return { ...updatedSale, paymentDate: data.paymentDate };
                    } else {
                        return sale;
                    }
                });
                handleClose({ updatedSales: newSales });
            } else {
                setSubmitPending(false);
            }
        }
    };

    const handleTriggerSubmit = async (appliedToAll: boolean) => {
        if (formRef.current) {
            await setWantsAppliedToAll(appliedToAll);
            const submitEvent = new Event("submit", { cancelable: true, bubbles: true });
            formRef.current.dispatchEvent(submitEvent);
        }
    };

    const confirmMenuActions: Action[] = [
        {
            name: t("TransferTerms.PaymentModal.ConfirmForAll"),
            action: () => handleTriggerSubmit(true),
        },
        {
            name: t("TransferTerms.PaymentModal.ConfirmForOne"),
            action: () => handleTriggerSubmit(false),
        },
    ];

    const wantsToRevert = watch("status") === 5;
    const paymentsLinked = termSale?.metadata.bulkActionUUID;

    /**
     * Display transfer checkbox line, if mode is "new", there are Rights Transfer channels and the payment is of type sales.
     */
    const displayOptionTransfer =
        formMode === "new" && !!channelOptions.length && termSale && termSale.type === "sales";

    /**
     * Display transfer details, if they checked the box.
     */
    const displayTransferDetails = wantsTransferRights && displayOptionTransfer;

    return (
        <form className={styles["payment-date-modal"]} onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            {paymentsLinked && (
                <MessageBlock
                    text={t("TransferTerms.PaymentModal.LinkedPayments")}
                    type="info"
                    link="https://helpdesk.crealo.app/fr/article/renseigner-des-droits-par-cessionnaire-z3xxr7/"
                    removeMarginFromDoc
                />
            )}
            {formMode === "edit" && (
                <SelectFormComponent
                    label={t("TransferTerms.PaymentModal.ChangeStatusQuestion")}
                    control={control}
                    controlName="status"
                    items={selectOptions}
                />
            )}
            <DateFormComponent
                control={control}
                controlName="paymentDate"
                label={
                    formMode === "new"
                        ? t("TransferTerms.PaymentModal.DateLabelQuestion")
                        : t("TransferTerms.PaymentModal.DateLabel")
                }
                required
                error={errors.paymentDate}
                disabled={wantsToRevert}
            />

            {/** If there are no Rights Transfer channels, display info. */}
            {!channelOptions.length && termSale && termSale.type === "sales" && (
                <MessageBlock text={t("TransferTerms.PaymentModal.NoChannel")} type="warning" />
            )}

            {displayOptionTransfer && (
                <CheckboxQuestion
                    checked={wantsTransferRights}
                    onChange={(e) => setWantsTransferRights((prev) => !prev)}
                    text={t("TransferTerms.PaymentModal.AutoTransfer")}
                    textStyle={{ color: variables["foreground"], fontWeight: 500 }}
                />
            )}

            {displayTransferDetails && (
                <div className={styles["transfer-rights-option"]}>
                    {!paymentsLinked && (
                        <h3>
                            {t("TransferTerms.PaymentModal.TransferInfo1")}
                            <span>{formatNumber(termSale?.netAmount / 100, { isCurrency: true })}</span>
                            {t("TransferTerms.PaymentModal.TransferInfo2")}
                            <span>{transferWorkLabel(termSale ?? {})}.</span>
                        </h3>
                    )}

                    <SelectFormComponent
                        control={control}
                        controlName="channelId"
                        label={t("TransferTerms.PaymentModal.ChooseChannel")}
                        items={channelOptions}
                    />
                </div>
            )}

            <div className={styles["buttons"]}>
                <StyledButton buttonType="outlined" onClick={() => handleClose({})}>
                    {t("TransferTerms.PaymentModal.Cancel")}
                </StyledButton>
                {paymentsLinked ? (
                    <ButtonMenu
                        mainActions={{
                            name: t("TransferTerms.PaymentModal.Confirm"),
                        }}
                        actions={confirmMenuActions}
                        showExpandBtn={false}
                        loading={submitPending}
                    />
                ) : (
                    <StyledButton buttonType="primary" type="submit" loading={submitPending}>
                        {t("TransferTerms.PaymentModal.Confirm")}
                    </StyledButton>
                )}
            </div>
        </form>
    );
};

export default PaymentDateModal;
